import { useState, useRef, useEffect } from "react";
import { api } from "@/lib/api";
import { MessageSquare, X, Send, Loader2 } from "lucide-react";

const WELCOME = {
  role: "assistant",
  content:
    "I am Porcelain — your dental sculpting tutor. Ask me about wax-up technique, cusp placement, occlusal grooves, morphology of any FDI tooth, or anything dental anatomy.",
};

export default function AiTutor() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/tutor/chat", { message: text, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "The tutor is briefly unavailable. Please try again in a moment." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating toggle */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 right-6 z-50 bg-primary text-primary-foreground w-14 h-14 flex items-center justify-center shadow-2xl hover:scale-105 transition-transform"
        style={{ borderRadius: 2 }}
        data-testid="ai-tutor-toggle"
        aria-label="Open AI Tutor"
      >
        {open ? <X className="w-5 h-5" /> : <MessageSquare className="w-5 h-5" />}
      </button>

      {open && (
        <div
          className="fixed bottom-24 right-6 z-50 w-[380px] max-w-[calc(100vw-2rem)] h-[540px] max-h-[80vh] bg-white border border-border shadow-2xl flex flex-col"
          style={{ borderRadius: 2 }}
          data-testid="ai-tutor-panel"
        >
          <div className="bg-primary text-primary-foreground px-4 py-3 flex items-center justify-between">
            <div>
              <div className="font-serif-display text-xl leading-none">Porcelain</div>
              <div className="font-mono-label text-[0.6rem] opacity-80 mt-1">AI Sculpting Tutor</div>
            </div>
            <div className="font-mono-label text-[0.6rem] opacity-80">CLAUDE 4.5</div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-background">
            {messages.map((m, i) => (
              <div
                key={i}
                className={`text-sm leading-relaxed ${m.role === "user" ? "ml-auto bg-foreground text-background" : "bg-white border border-border"}`}
                style={{ borderRadius: 2, padding: "0.65rem 0.85rem", maxWidth: "90%" }}
                data-testid={`tutor-msg-${m.role}-${i}`}
              >
                {m.content}
              </div>
            ))}
            {loading && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground font-mono-label" data-testid="tutor-loading">
                <Loader2 className="w-3 h-3 animate-spin" /> Thinking…
              </div>
            )}
          </div>

          <form onSubmit={send} className="border-t border-border p-3 flex gap-2 bg-white">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about cusp placement, wax-up, FDI 36…"
              className="flex-1 bg-background border border-border px-3 py-2 text-sm outline-none focus:border-primary"
              style={{ borderRadius: 2 }}
              data-testid="tutor-input"
              disabled={loading}
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="bg-primary text-primary-foreground px-3 flex items-center justify-center disabled:opacity-50"
              style={{ borderRadius: 2 }}
              data-testid="tutor-send-button"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
